import base64url from 'base64-url';
import {format as formatDate} from 'date-fns';
import {SymbolicDate} from '../enums';
import {Coordinate, FeatureFilter, LayerId, Params} from '../types';
import {serializeParams} from './serializeParams';
import {hasParams} from './hasParams';
// Toto je workaround pro opravu dlouhodobě nevyřešeného issue https://github.com/brianloveswords/base64url/issues/33
import {Buffer} from 'buffer';
global.Buffer = global.Buffer || Buffer;

/**
 * Parametry pro sestavení URL do mapio.
 *
 * @ignore
 */
interface MapioUrlOptions {
    /**
     * Cesta ke stránce v URL.
     */
    path?: string;

    /**
     * Query parametry v URL.
     */
    queryParams?: Params;

    /**
     * Kontextové parametry pro aplikační konektory.
     */
    contextParams?: Params;

    /**
     * Unikátní ID instance mapio.
     */
    instanceId?: string;

    /**
     * Téma konfigurace.
     */
    topic?: string;

    /**
     * ID podkladové vrstvy.
     */
    backgroundLayer?: LayerId | null;

    /**
     * ID popisné vrstvy.
     */
    foregroundLayer?: LayerId | null;

    /**
     * Pole ID viditelných datových vrstev.
     */
    layers?: LayerId[];

    /**
     * Pole ID otevřených uzlů katalogu.
     */
    catalogNodes?: string[];

    /**
     * Souřadnice středu mapy.
     */
    location?: Coordinate;

    /**
     * Zoom mapy.
     */
    zoom?: number;

    /**
     * Filtr pro features v datových vrstvách.
     */
    filter?: FeatureFilter;

    /**
     * Příznak, zda je povoleno uživatelsky přepínat téma.
     */
    topicChangeEnabled?: boolean;
}

/**
 * Sestaví URL do mapio.
 *
 * @param serverUrl - URL serveru.
 * @param options - Volitelné parametry.
 * @returns Výsledná URL.
 * @ignore
 */
export function buildMapioUrl(serverUrl: string, options: MapioUrlOptions = {}): string {
    const {
        path,
        queryParams,
        contextParams,
        instanceId,
        topic,
        backgroundLayer,
        foregroundLayer,
        layers,
        catalogNodes,
        location,
        zoom,
        filter,
        topicChangeEnabled,
    } = options;

    let url = serverUrl;
    let urlQuery = queryParams ? {...queryParams} : {};

    if (url[url.length - 1] === '/') {
        url = url.substring(0, url.length - 1);
    }

    if (path) {
        if (path[0] !== '/') {
            url += '/';
        }

        url += path;

        if (url[url.length - 1] === '/') {
            url = url.substring(0, url.length - 1);
        }
    }

    if (hasParams(contextParams)) {
        urlQuery.context = base64url.encode(serializeParams(contextParams!));
    }

    if (instanceId != null) {
        urlQuery.instanceId = encodeURIComponent(instanceId);
    }

    if (topic != null) {
        urlQuery.topic = encodeURIComponent(topic);
    }

    if (backgroundLayer !== undefined) {
        urlQuery.bgLayer = encodeURIComponent(backgroundLayer || ''); // Null => vypnutá vrstva
    }

    if (foregroundLayer !== undefined) {
        urlQuery.fgLayer = encodeURIComponent(foregroundLayer || ''); // Null => vypnutá vrstva
    }

    if (layers) {
        urlQuery.layers = encodeURIComponent(layers.join(','));
    }

    if (catalogNodes) {
        urlQuery.catalogNodes = encodeURIComponent(catalogNodes.join(','));
    }

    if (location && location[0] != null && location[1] != null) {
        urlQuery.lon = encodeURIComponent(location[0]);
        urlQuery.lat = encodeURIComponent(location[1]);
    }

    if (zoom != null) {
        urlQuery.zoom = encodeURIComponent(zoom);
    }

    if (filter != null) {
        const date = getDateQueryValue(filter);
        if (date) {
            urlQuery.date = encodeURIComponent(date);
        }
    }

    if (topicChangeEnabled != null) {
        urlQuery.topicChangeEnabled = encodeURIComponent(topicChangeEnabled);
    }

    if (hasParams(urlQuery)) {
        url += '?' + serializeParams(urlQuery);
    }

    return url;
}

/**
 * Konstanta udávající neomezený rozsah v datumovém filtru.
 *
 * @ignore
 */
const unlimitedDateQueryValue = '*';

/**
 * Převod na symbolické datumové konstanty použivé v query URL.
 *
 * @ignore
 */
const symbolicDateQueryValues = {
    [SymbolicDate.TODAY]: 'd',
    [SymbolicDate.TODAY_PLUS_WEEK]: 'd+w',
    [SymbolicDate.TODAY_PLUS_TWO_WEEKS]: 'd+2w',
    [SymbolicDate.TODAY_PLUS_MONTH]: 'd+m',
    [SymbolicDate.TODAY_AND_FUTURE]: 'd+f',
    [SymbolicDate.TODAY_PLUS_MINUS_WEEK]: 'd+w-w',
    [SymbolicDate.THIS_WEEK]: 'w',
    [SymbolicDate.THIS_MONTH]: 'm',
    [SymbolicDate.THIS_YEAR]: 'y',
    [SymbolicDate.NEXT_WEEK]: 'nw',
    [SymbolicDate.NEXT_MONTH]: 'nm',
    [SymbolicDate.NEXT_YEAR]: 'ny',
};

/**
 * Vrátí hodnotu datumového filtru ve formě řetězce použitelného v URL.
 *
 * @param filter - Datový filtr pro features ve vrstvách.
 * @returns Výsledný řetězec nebo null.
 * @ignore
 */
function getDateQueryValue(filter: FeatureFilter): string | undefined {
    let {dateFrom, dateTo} = filter;
    const {date} = filter;

    if (date !== undefined) {
        if (Array.isArray(date)) {
            [dateFrom, dateTo] = date;
        } else if (date instanceof Date) {
            return formatDateQueryValue(date);
        } else if (date) {
            return symbolicDateQueryValues[date];
        } else {
            return unlimitedDateQueryValue;
        }
    }

    if (dateFrom !== undefined || dateTo !== undefined) {
        if (dateFrom || dateTo) {
            return formatDateQueryValue(dateFrom) + ',' + formatDateQueryValue(dateTo);
        }
        return unlimitedDateQueryValue;
    }

    return undefined; // V query se nic nenastaví, použije se výchozí hodnota v mapio
}

/**
 * Naformátuje datum pro použití v URL.
 *
 * @param date - Datum.
 * @returns Výsledný řetězec.
 * @ignore
 */
function formatDateQueryValue(date?: Date | null): string {
    return date instanceof Date ? formatDate(date, 'yyyy-MM-dd') : '';
}
